import {
    Avatar,
    Box,
    Divider,
    IconButton,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    ListSubheader,
    Tooltip,
    Typography,
} from "@mui/material";
import {alpha} from "@mui/material/styles";
import {Link as RouterLink, useLocation} from "react-router-dom";
import {useTranslation} from "react-i18next";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import AssignmentIndOutlinedIcon from "@mui/icons-material/AssignmentIndOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

import {useAuth} from "../context/AuthContext";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";

type Props = {
    onNavigate?: () => void;
    collapsed?: boolean;
    onToggleCollapse?: () => void;
};

const SideMenu = ({onNavigate, collapsed = false, onToggleCollapse}: Props) => {
    const {t} = useTranslation();
    const {hasRole, user} = useAuth();
    const location = useLocation();

    const sections = [
        ...(hasRole("ROLE_STUDENT") || hasRole("ROLE_TEACHER")
            ? [{
                title: t("sidemenu.learning"),
                items: [
                    {label: t("sidemenu.myCourses"), path: "/my-courses", icon: <SchoolOutlinedIcon/>},
                    {label: t("sidemenu.myStreams"), path: "/my-streams", icon: <GroupsOutlinedIcon/>},
                    {label: t("sidemenu.mySchedule"), path: "/my-schedule", icon: <CalendarMonthOutlinedIcon/>},
                ],
            }]
            : []),

        ...(hasRole("ROLE_ADMIN")
            ? [{
                title: t("sidemenu.administration"),
                items: [
                    {label: t("sidemenu.courses"), path: "/courses", icon: <MenuBookOutlinedIcon/>},
                    {label: t("sidemenu.users"), path: "/users", icon: <PeopleOutlineIcon/>},
                    {label: t("sidemenu.streams"), path: "/admin/streams", icon: <AssignmentIndOutlinedIcon/>},
                    {label: t("sidemenu.companies"), path: "/companies", icon: <BusinessOutlinedIcon/>},
                    {label: t("sidemenu.logoSettings"), path: "/course-logos", icon: <ImageOutlinedIcon/>},
                    // {label: "Live Test", path: "/live-test", icon: <CalendarMonthIcon/>}
                ],
            }]
            : []),
    ];

    const isSelected = (path: string) =>
        location.pathname === path || location.pathname.startsWith(path + "/");

    const initials = (user?.fullName || "")
        .trim()
        .split(/\s+/)
        .slice(0, 2)
        .map((part) => part[0] ?? "")
        .join("")
        .toUpperCase() || "U";

    return (
        <Box sx={{width: "100%", overflow: "hidden"}}>
            <Box
                sx={{
                    mb: collapsed ? 1 : 2,
                    p: collapsed ? 0.5 : 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: collapsed ? "center" : "space-between",
                    gap: 1,
                    transition: "all 220ms ease",
                }}
            >
                {!collapsed && (
                    <Box sx={{display: "flex", alignItems: "center", gap: 1.25, minWidth: 0}}>
                        <Avatar
                            sx={(theme) => ({
                                width: 38,
                                height: 38,
                                fontSize: 14,
                                fontWeight: 700,
                                bgcolor: alpha(theme.palette.primary.main, 0.14),
                                color: theme.palette.primary.main,
                            })}
                        >
                            {initials}
                        </Avatar>
                        <Box sx={{minWidth: 0}}>
                            <Typography variant="caption" color="text.secondary" component="div">
                                {t("sidemenu.welcome")}
                            </Typography>
                            <Typography variant="subtitle2" noWrap>
                                {user?.fullName ?? t("sidemenu.defaultUser")}
                            </Typography>
                        </Box>
                    </Box>
                )}

                {onToggleCollapse ? (
                    <Tooltip
                        title={collapsed ? t("sidemenu.showSidebar") : t("sidemenu.hideSidebar")}
                        placement="right"
                    >
                        <IconButton
                            onClick={onToggleCollapse}
                            size="small"
                            sx={(theme) => ({
                                border: `1px solid ${theme.palette.divider}`,
                                bgcolor: "background.paper",
                                "&:hover": {
                                    bgcolor: theme.palette.action.hover,
                                },
                            })}
                        >
                            {collapsed ? <ChevronRightIcon fontSize="small"/> : <ChevronLeftIcon fontSize="small"/>}
                        </IconButton>
                    </Tooltip>
                ) : null}
            </Box>

            {sections.map((section, index) => (
                <List
                    key={section.title}
                    sx={{p: collapsed ? 0.5 : 1, pt: index === 0 ? undefined : 0}}
                    subheader={
                        collapsed ? (
                            index > 0 ? <Divider sx={{my: 1}}/> : undefined
                        ) : (
                            <ListSubheader
                                disableSticky
                                sx={{
                                    px: 2,
                                    lineHeight: "32px",
                                    bgcolor: "transparent",
                                    color: "text.secondary",
                                    fontSize: 11,
                                    fontWeight: 700,
                                    letterSpacing: "0.08em",
                                    textTransform: "uppercase",
                                }}
                            >
                                {section.title}
                            </ListSubheader>
                        )
                    }
                >
                    {section.items.map((item) => {
                        const selected = isSelected(item.path);

                        const button = (
                            <ListItemButton
                                key={item.path}
                                component={RouterLink}
                                to={item.path}
                                onClick={onNavigate}
                                selected={selected}
                                sx={{
                                    mb: 0.25,
                                    minHeight: 42,
                                    justifyContent: collapsed ? "center" : "flex-start",
                                    px: collapsed ? 1 : 2,
                                    transition: "all 220ms ease",
                                }}
                            >
                                <ListItemIcon
                                    sx={{
                                        minWidth: collapsed ? 0 : 36,
                                        justifyContent: "center",
                                        color: "inherit",
                                        transition: "all 220ms ease",
                                    }}
                                >
                                    {item.icon}
                                </ListItemIcon>

                                {!collapsed && (
                                    <ListItemText
                                        primary={item.label}
                                        slotProps={{
                                            primary: {
                                                noWrap: true,
                                            },
                                        }}
                                    />
                                )}
                            </ListItemButton>
                        );

                        return collapsed ? (
                            <Tooltip key={item.path} title={item.label} placement="right">
                                {button}
                            </Tooltip>
                        ) : button;
                    })}
                </List>
            ))}
        </Box>
    );
};

export default SideMenu;